import React, { useState, useEffect } from 'react';
import { Box, List, ListItem, ListItemAvatar, ListItemText, Popover, Skeleton, Stack, Typography } from '@mui/material';
import styles from '../../../../custom/style.module.css';
import flightStyles from '../../flights/tabs/flightreport/reports.module.css';
import { dateTolocale } from '../../../../utils/converter';
import http from '../../../../utils/http';
import { extractChangedLines } from './diffUtils';
import classes from '../docFwb.module.css';

const getBadgeColor = (type, op) => {
  if (type?.includes('AI') || op === 'ai_refine') return '#0284c7';
  if (op === 'generate') return '#16a34a';
  if (op === 'select_main') return '#9333ea';
  if (op === 'create_version') return '#092b4a';
  return '#eab308';
};

const getBadgeLabel = (type, op) => {
  if (type?.includes('AI')) return 'AI Refined';
  if (op === 'generate') return 'Generated';
  if (op === 'select_main') return 'Main Selected';
  if (op === 'create_version') return 'New Version';
  return 'Manual Edit';
};

const FwbHistoryPopover = ({
  open = false,
  anchorEl = null,
  onClose,
  docId = null,
  versions = [],
  localLogs = [],
}) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState({});

  useEffect(() => {
    if (open && docId) {
      setLoading(true);
      http
        .get(`/doc_fwb/history/${docId}`)
        .then((res) => {
          if (res?.data?.data) {
            setLogs(res.data.data);
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching FWB history:', err);
          setLoading(false);
        });
    }
  }, [open, docId]);

  const toggleExpand = (logId) => {
    setExpandedLogs((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  };

  const displayLogs = [
    ...(localLogs || []),
    ...logs.filter(
      (remote) =>
        !localLogs.some(
          (local) =>
            local.details?.version &&
            remote.details?.version &&
            String(local.details.version) === String(remote.details.version) &&
            local.msg === remote.msg
        )
    ),
  ];

  return (
    <Popover
      open={Boolean(open && anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <List
        sx={{
          width: '100%',
          maxWidth: 440,
          minWidth: 350,
          bgcolor: 'background.paper',
          p: 2,
          maxHeight: 460,
          minHeight: 160,
          overflowY: 'auto',
        }}
      >
        {!loading ? (
          displayLogs.length > 0 ? (
            displayLogs.map((row, index) => {
              const badgeBg = getBadgeColor(row.type, row.operation);
              const badgeText = getBadgeLabel(row.type, row.operation);
              const userName = row.user?.name || 'System';
              const userRole = row.user?.role ? ` (${row.user.role})` : '';
              const createdDate = dateTolocale(row.created_at, 'DD MMM YYYY HH:mm');

              let changedLines = [];
              if (Array.isArray(row.details?.changes) && row.details.changes.length > 0) {
                changedLines = row.details.changes;
              } else if (row.prev_details?.fwb && row.details?.fwb) {
                changedLines = extractChangedLines(row.prev_details.fwb, row.details.fwb);
              } else if (versions && versions.length > 0 && row.details?.version) {
                const curVerNum = Number(row.details.version);
                const curVerObj = versions.find((v) => Number(v.version) === curVerNum);
                const prevVerObj = versions.find((v) => Number(v.version) === (Number(row.prev_details?.version) || curVerNum - 1));
                if (curVerObj?.fwb && prevVerObj?.fwb) {
                  changedLines = extractChangedLines(prevVerObj.fwb, curVerObj.fwb);
                }
              }

              const addedCount = changedLines.filter((d) => d.type === 'added').length;
              const removedCount = changedLines.filter((d) => d.type === 'removed').length;
              const logKey = row.id || index;
              const isExpanded = Boolean(expandedLogs[logKey]);
              const visibleChanges = isExpanded ? changedLines : changedLines.slice(0, 4);

              return (
                <ListItem
                  key={logKey}
                  className={flightStyles.historyListItem}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    borderBottom: '1px solid #f1f5f9',
                    py: 1.5,
                  }}
                >
                  <Stack direction='row' alignItems='center' width='100%' spacing={1.5}>
                    <ListItemAvatar className={styles.mr2} sx={{ minWidth: 'auto' }}>
                      <Typography
                        variant='span'
                        color='white'
                        bgcolor={badgeBg}
                        textTransform='uppercase'
                        fontSize='10px'
                        fontWeight={800}
                        px={0.75}
                        py={0.25}
                        borderRadius='4px'
                        lineHeight='normal'
                        minWidth='75px'
                        display='block'
                        textAlign='center'
                      >
                        {badgeText}
                      </Typography>
                    </ListItemAvatar>
                    <ListItemText
                      className={flightStyles.historyListText}
                      primaryTypographyProps={{
                        fontSize: 12,
                        color: 'primary.main',
                        fontWeight: 600,
                      }}
                      secondaryTypographyProps={{ fontSize: 11 }}
                      primary={row.msg || `${badgeText} applied`}
                      secondary={`${createdDate} | ${userName}${userRole}`}
                    />
                  </Stack>
                  {/* Line-by-Line Changes Diff Display */}
                  {changedLines.length > 0 && (
                    <Box sx={{
                      width: '100%',
                      mt: 1,
                      pl: 0.5 
                    }}>
                      <Stack direction='row' spacing={1} alignItems='center' mb={0.5}>
                        <Typography variant='caption' fontWeight={700} fontSize='10.5px' color='text.secondary'>
                          Recorded Modifications:
                        </Typography>
                        <span className={classes.diffBadgeAdded} style={{
                          fontSize: '10px',
                          padding: '1px 5px' 
                        }}>
                          +{addedCount}
                        </span>
                        <span className={classes.diffBadgeRemoved} style={{
                          fontSize: '10px',
                          padding: '1px 5px' 
                        }}>
                          -{removedCount}
                        </span>
                      </Stack>

                      <div className={classes.historyDiffBox}>
                        {visibleChanges.map((change, cIdx) => {
                          const isAdd = change.type === 'added';
                          const lineNum = change.lineNumber || (isAdd ? change.newLineNumber : change.oldLineNumber);
                          return (
                            <div
                              key={`${cIdx}-${change.type}-${lineNum}`}
                              className={isAdd ? classes.historyDiffRowAdded : classes.historyDiffRowRemoved}
                            >
                              <span style={{
                                userSelect: 'none',
                                minWidth: '60px',
                                fontWeight: 600,
                              }}>
                                {lineNum ? `Line ${lineNum} ` : ''}{isAdd ? '+' : '-'}
                              </span>
                              <span>{change.line}</span>
                            </div>
                          );
                        })}
                      </div>

                      {changedLines.length > 4 && (
                        <button
                          type='button'
                          className={classes.historyDiffToggleBtn}
                          onClick={() => toggleExpand(logKey)}
                        >
                          {isExpanded
                            ? 'Show fewer lines'
                            : `View all ${changedLines.length} changed lines`}
                        </button>
                      )}
                    </Box>
                  )}
                </ListItem>
              );
            })
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 4,
              }}
            >
              <Typography variant='span' color='textSecondary' fontSize='0.8rem'>
                No History Found
              </Typography>
            </Box>
          )
        ) : (
          Array.from({ length: 3 }).map((_, index) => (
            <ListItem key={index}>
              <Skeleton variant='rectangular' width={320} height={50} sx={{ borderRadius: '6px' }} />
            </ListItem>
          ))
        )}
      </List>
    </Popover>
  );
};

export default FwbHistoryPopover;

